import { ClassList, addClassToList } from "./ClassList";
import { LayoutEntity } from "./Definitions";

const MediaSpecificBreakpoints = ["all", "mobile", "ipad", "desktop"] as const;

type MediaSpecificBreakpoint = typeof MediaSpecificBreakpoints[number];

export type MediaSpecific<T> = { [Key in MediaSpecificBreakpoint]?: T };

const mediaSpecificSuffixes: Record<MediaSpecificBreakpoint, string> = {
    all: "",
    mobile: "--mobile",
    ipad: "--ipad",
    desktop: "--desktop",
}

type EntityCheck<Entity> = (entity: Entity) => boolean;
type EntityOrCheck<Entity> = Entity | EntityCheck<Entity>;

const isEntitySW = <Entity>(entity: Entity | undefined, value: EntityOrCheck<Entity>): boolean =>
    entity !== undefined && (typeof value === "function" ? (value as EntityCheck<Entity>)(entity) : entity === value);

export const mediaSpecificClass = <Entity extends number, LayoutKey extends string>(entity: Entity | MediaSpecific<Entity | LayoutEntity<Entity, LayoutKey>> | undefined, layoutKey: LayoutKey, onEntity: (entity: Entity, suffix: string) => string): ClassList =>
{
    const classList: ClassList = [];

    if (typeof entity === "object")
    {
        MediaSpecificBreakpoints.forEach(key => 
        {
            const item = entity[key];
            const value = typeof item === "object" ? item[layoutKey] : item;
            if (value !== undefined)
            {
                classList.push(onEntity(value, mediaSpecificSuffixes[key]));
            }
        });
    }
    else if (entity !== undefined)
    {
        classList.push(onEntity(entity, ""));
    }

    return classList;
}

export const mediaSpecificClassM = <Entity extends number, LayoutKey extends string>(entity: Entity | MediaSpecific<Entity | LayoutEntity<Entity, LayoutKey>> | undefined, layoutKey: LayoutKey, classPrefix: string, map: Record<Entity, string>, onClass?: (className: string) => string): ClassList =>
    mediaSpecificClass(entity, layoutKey, (entity, suffix) => 
    {
        const className = `${classPrefix}-${map[entity]}${suffix}`;
        return onClass !== undefined ? onClass(className) : className;
    });

export const mediaSpecificClassO = <Entity>(entity: MediaSpecific<Entity> | undefined, onEntity: (entity: Entity, suffix: string) => ClassList | string | undefined): ClassList =>
{
    const classList: ClassList = [];

    if (entity !== undefined)
    {
        MediaSpecificBreakpoints.forEach(key => 
        {
            const item = entity[key];
            if (item !== undefined)
            {
                addClassToList(classList, onEntity(item, mediaSpecificSuffixes[key]));
            }
        });
    }

    return classList;
}

export const mediaSpecificClassSW = <Entity>(className: string, mediaSpecific: MediaSpecific<Entity> | undefined, value: EntityOrCheck<Entity>, onClass?: (className: string) => string): ClassList =>
{
    const classList: ClassList = [];

    if (mediaSpecific !== undefined)
    {
        MediaSpecificBreakpoints.forEach(key => 
        {
            const item = mediaSpecific[key];
            if (isEntitySW(item, value))
            {
                className += mediaSpecificSuffixes[key];
                className = onClass !== undefined ? onClass(className) : className;
                classList.push(className);
            }
        });
    }

    return classList;
}

export const mediaSpecificClassSWL = <Entity, Key extends string, Layout extends LayoutEntity<Entity, Key>>(className: string, mediaSpecific: MediaSpecific<Layout> | undefined, key: Key, value: EntityOrCheck<Layout[Key]>, onClass?: (className: string) => string): ClassList =>
    mediaSpecificClassSW(className, mediaSpecific, layout => isEntitySW(layout[key], value), onClass);

export const mediaSpecificMerge = <Entity, TargetLayoutKey extends string, TargetLayout extends LayoutEntity<Entity, TargetLayoutKey>, SourceLayoutKey extends string, SourceLayout extends LayoutEntity<Entity, SourceLayoutKey>>
    (target: MediaSpecific<TargetLayout> | undefined, targetLayoutKey: TargetLayoutKey, source: MediaSpecific<SourceLayout> | undefined, sourceLayoutKey: SourceLayoutKey, forceOverride: boolean = false): MediaSpecific<TargetLayout> | undefined =>
{
    MediaSpecificBreakpoints.forEach(key => 
    {
        const sourceValue = source?.[key]?.[sourceLayoutKey];
        if (sourceValue !== undefined)
        {
            const targetLayout = target?.[key];
            const targetValueOld = targetLayout?.[targetLayoutKey];
            const targetValueNew = forceOverride ? sourceValue ?? targetValueOld : targetValueOld ?? sourceValue;

            if (targetValueNew !== targetValueOld)
            {
                target = { ...target, [key]: { ...targetLayout, [targetLayoutKey]: targetValueNew } };
            }
        }
    });

    return target;
}
